<template>
    <div class="page-gutter">
        <div v-if="isLoading" style="display: flex; justify-content: center;">
            <div class="loader" style="margin-top: 400px;"></div>
        </div>

        <div v-if="!isLoading">
            <!-- Unified section head with a right-aligned action -->
            <Section :title="$t('demo.store.title', { store: 'Newell DIY' })">
                <!-- Content -->
                <storalytic-demo-fixed-widgets
                :fixed-widgets-data="fixedWidgetData"
                :fixed-widgets-compare-to-data="fixedWidgetCompareToData"
                />

            </Section>
        </div>

    </div>
</template>
  
<script>
import { ref, onMounted, defineComponent } from 'vue'
import { useRoute } from 'vue-router'

import Section from '@/components/ui/Section.vue'
import StoralyticDemoFixedWidgets from './StoralyticDemoFixedWidgets.vue'
import { loadDemoJSON } from '@/api/demoData.js'
import { changeFor, withoutZoneInType } from '@/demo/changesTodayDemo'

export default defineComponent({
    name: 'Dashboard',

    components: {
        Section,
        StoralyticDemoFixedWidgets,
    },

    setup() {
        const dateRangeMode = ref('duration')
        const clearDatePicker = ref(null)
        const dataGranularity = ref('daily')
        const fixedWidgetData = ref({})
        const fixedWidgetCompareToData = ref({})
        
        const isLoading = ref(true)

        const loadJSON = (path, opts) => loadDemoJSON(path, opts)
        // ?scenario=type_change: a zone changed type today and leaves its old type (6.2).
        const route = useRoute()
        const _change = changeFor(route.query)
        const scenarioed = (d) => (_change ? withoutZoneInType(d, _change.zone, _change.from) : d)

        onMounted(async () => {
            await fetchFixedWidgetData()
            isLoading.value = false
        })

        const fetchFixedWidgetData = async () => {
            try {
                // 1) Read the fixed snapshot from /public/demo_data/demo_fixed_widgets.json
                //    (this file is your export of:
                //     company_fixed_widget_data_only_regular_opening_hours:{companyId}:past_30_days)
                const fullData = await loadJSON('/demo_data/demo_fixed_widgets.json')

                const newStoredData = {
                    store_total_visitors: fullData.store_total_visitors,
                    current_weekday: fullData.current_weekday,
                    average_same_weekday: Math.round(fullData.average_same_weekday),
                    average_visitors: Math.round(fullData.average_visitors),
                    highest_total_hour: fullData.highest_total_hour,
                    highest_total_hour_count: fullData.highest_total_hour_count,
                    highest_total_day: fullData.highest_total_day,
                    highest_total_day_count: fullData.highest_total_day_count,
                    busiest_hour: fullData.busiest_hour,
                    average_on_busiest_hour: Math.round(fullData.average_on_busiest_hour),
                    trend: fullData.trend,
                    
                    best_product_zone_by_visitors: fullData.best_product_zone_by_visitors,
                    best_product_zone_by_avg_duration: fullData.best_product_zone_by_avg_duration,
                    worst_product_zone_by_visitors: fullData.worst_product_zone_by_visitors,
                    total_duration_per_product_zone: fullData.total_duration_per_product_zone,
                    percentage_by_duration_range_in_product_zone: fullData.percentage_by_duration_range_in_product_zone,
                    product_zone_visitors_counts: fullData.product_zone_visitors_counts,
                    product_zone_clear_lingerers_counts: fullData.product_zone_clear_lingerers_counts,
                    product_hotcold_by_visitors: fullData.product_hotcold_by_visitors,
                    product_hotcold_by_clear: fullData.product_hotcold_by_clear,
                    product_zone_funnels: fullData.product_zone_funnels,

                    best_demo_zone_by_visitors: fullData.best_demo_zone_by_visitors,
                    best_demo_zone_by_avg_duration: fullData.best_demo_zone_by_avg_duration,
                    worst_demo_zone_by_visitors: fullData.worst_demo_zone_by_visitors,
                    total_duration_per_demo_zone: fullData.total_duration_per_demo_zone,
                    percentage_by_duration_range_in_demo_zone: fullData.percentage_by_duration_range_in_demo_zone,
                    demo_zone_visitors_counts: fullData.demo_zone_visitors_counts,
                    demo_zone_clear_lingerers_counts: fullData.demo_zone_clear_lingerers_counts,
                    demo_hotcold_by_visitors: fullData.demo_hotcold_by_visitors,
                    demo_hotcold_by_clear: fullData.demo_hotcold_by_clear,
                    demo_zone_funnels: fullData.demo_zone_funnels,

                    best_display_zone_by_visitors: fullData.best_display_zone_by_visitors,
                    best_display_zone_by_avg_duration: fullData.best_display_zone_by_avg_duration,
                    worst_display_zone_by_visitors: fullData.worst_display_zone_by_visitors,
                    total_duration_per_display_zone: fullData.total_duration_per_display_zone,
                    percentage_by_duration_range_in_display_zone: fullData.percentage_by_duration_range_in_display_zone,
                    display_zone_visitors_counts: fullData.display_zone_visitors_counts,
                    display_zone_clear_lingerers_counts: fullData.display_zone_clear_lingerers_counts,
                    display_hotcold_by_visitors: fullData.display_hotcold_by_visitors,
                    display_hotcold_by_clear: fullData.display_hotcold_by_clear,
                    display_zone_funnels: fullData.display_zone_funnels,

                    average_waiting_time: fullData.average_waiting_time,
                    average_time_in_zone_waiting: fullData.average_time_in_zone_waiting,
                    max_waiting_duration: fullData.max_waiting_duration,
                    max_waiting_zone: fullData.max_waiting_zone,
                    most_typical_waiting_time: fullData.most_typical_waiting_time,
                    real_wait_rate: fullData.real_wait_rate,
                    real_wait_count: fullData.real_wait_count,
                    average_interaction_time: fullData.average_interaction_time,
                    average_time_in_zone_interaction: fullData.average_time_in_zone_interaction,
                    max_interaction_duration: fullData.max_interaction_duration,
                    max_interaction_zone: fullData.max_interaction_zone,
                    most_typical_interaction_time: fullData.most_typical_interaction_time,
                    real_transaction_rate: fullData.real_transaction_rate,
                    real_transaction_count: fullData.real_transaction_count,
                    fastest_checkout_zone: fullData.fastest_checkout_zone,
                    slowest_checkout_zone: fullData.slowest_checkout_zone,
                    fastest_interaction_zone: fullData.fastest_interaction_zone,
                    slowest_interaction_zone: fullData.slowest_interaction_zone,
                    most_used_interaction_zone: fullData.most_used_interaction_zone,
                    least_used_interaction_zone: fullData.least_used_interaction_zone,

                    store_dwell_estimate_seconds: fullData.store_dwell_estimate_seconds,
                    store_dwell_confidence: fullData.store_dwell_confidence,
                    store_dwell_bias_pct: fullData.store_dwell_bias_pct,

                    service_zones_dwell_confidence: fullData.service_zones_dwell_confidence,
                    service_zones_dwell_bias_pct: fullData.service_zones_dwell_bias_pct,

                    product_zones_dwell_confidence: fullData.product_zones_dwell_confidence,
                    product_zones_dwell_bias_pct: fullData.product_zones_dwell_bias_pct,

                    checkout_interaction_usage_pct: fullData.checkout_interaction_usage_pct,
                    checkout_cashier_usage_pct: fullData.checkout_cashier_usage_pct,

                    // Checkout queue (line length from waiting-zone occupancy overlap).
                    // Demo values sampled from real store cid 3.
                    checkout_queue: {
                        checkouts: 3,
                        mean_line: 0.2,
                        peak_line: 5,
                        pct_time_ge2: 3.1,
                        busiest_checkout_ge2: 7.3,
                        avg_line_while_serving: 0.5,
                    },

                    // Capacity gauge's wasted-capacity math needs total_opening_hours
                    // (staffedHours = openingHours × lanes × capacityFrac).
                    total_opening_hours: fullData.total_opening_hours,

                    date: fullData.last_updated_date // Add today's date to track when the data was fetched
                }

                fixedWidgetData.value = scenarioed({ ...newStoredData })

            } catch (error) {
                console.error('Error fetching fixed widget data:', error)
            }
            try {
                    const fullCompareToData = await loadJSON('/demo_data/demo_fixed_widgets_compare_to.json')

                    const newStoredCompareToData = {
                        store_total_visitors: fullCompareToData.store_total_visitors,
                        current_weekday: fullCompareToData.current_weekday,
                        average_same_weekday: Math.round(fullCompareToData.average_same_weekday),
                        average_visitors: Math.round(fullCompareToData.average_visitors),
                        highest_total_hour: fullCompareToData.highest_total_hour,
                        highest_total_hour_count: fullCompareToData.highest_total_hour_count,
                        highest_total_day: fullCompareToData.highest_total_day,
                        highest_total_day_count: fullCompareToData.highest_total_day_count,
                        busiest_hour: fullCompareToData.busiest_hour,
                        average_on_busiest_hour: Math.round(fullCompareToData.average_on_busiest_hour),
                        trend: fullCompareToData.trend,
                        
                        best_product_zone_by_visitors: fullCompareToData.best_product_zone_by_visitors,
                        best_product_zone_by_avg_duration: fullCompareToData.best_product_zone_by_avg_duration,
                        worst_product_zone_by_visitors: fullCompareToData.worst_product_zone_by_visitors,
                        total_duration_per_product_zone: fullCompareToData.total_duration_per_product_zone,
                        percentage_by_duration_range_in_product_zone: fullCompareToData.percentage_by_duration_range_in_product_zone,
                        product_zone_visitors_counts: fullCompareToData.product_zone_visitors_counts,
                        product_zone_clear_lingerers_counts: fullCompareToData.product_zone_clear_lingerers_counts,
                        product_hotcold_by_visitors: fullCompareToData.product_hotcold_by_visitors,
                        product_hotcold_by_clear: fullCompareToData.product_hotcold_by_clear,
                        product_zone_funnels: fullCompareToData.product_zone_funnels,

                        best_demo_zone_by_visitors: fullCompareToData.best_demo_zone_by_visitors,
                        best_demo_zone_by_avg_duration: fullCompareToData.best_demo_zone_by_avg_duration,
                        worst_demo_zone_by_visitors: fullCompareToData.worst_demo_zone_by_visitors,
                        total_duration_per_demo_zone: fullCompareToData.total_duration_per_demo_zone,
                        percentage_by_duration_range_in_demo_zone: fullCompareToData.percentage_by_duration_range_in_demo_zone,
                        demo_zone_visitors_counts: fullCompareToData.demo_zone_visitors_counts,
                        demo_zone_clear_lingerers_counts: fullCompareToData.demo_zone_clear_lingerers_counts,
                        demo_hotcold_by_visitors: fullCompareToData.demo_hotcold_by_visitors,
                        demo_hotcold_by_clear: fullCompareToData.demo_hotcold_by_clear,
                        demo_zone_funnels: fullCompareToData.demo_zone_funnels,

                        best_display_zone_by_visitors: fullCompareToData.best_display_zone_by_visitors,
                        best_display_zone_by_avg_duration: fullCompareToData.best_display_zone_by_avg_duration,
                        worst_display_zone_by_visitors: fullCompareToData.worst_display_zone_by_visitors,
                        total_duration_per_display_zone: fullCompareToData.total_duration_per_display_zone,
                        percentage_by_duration_range_in_display_zone: fullCompareToData.percentage_by_duration_range_in_display_zone,
                        display_zone_visitors_counts: fullCompareToData.display_zone_visitors_counts,
                        display_zone_clear_lingerers_counts: fullCompareToData.display_zone_clear_lingerers_counts,
                        display_hotcold_by_visitors: fullCompareToData.display_hotcold_by_visitors,
                        display_hotcold_by_clear: fullCompareToData.display_hotcold_by_clear,
                        display_zone_funnels: fullCompareToData.display_zone_funnels,

                        average_waiting_time: fullCompareToData.average_waiting_time,
                        average_time_in_zone_waiting: fullCompareToData.average_time_in_zone_waiting,
                        max_waiting_duration: fullCompareToData.max_waiting_duration,
                        max_waiting_zone: fullCompareToData.max_waiting_zone,
                        most_typical_waiting_time: fullCompareToData.most_typical_waiting_time,
                        real_wait_rate: fullCompareToData.real_wait_rate,
                        real_wait_count: fullCompareToData.real_wait_count,
                        average_interaction_time: fullCompareToData.average_interaction_time,
                        average_time_in_zone_interaction: fullCompareToData.average_time_in_zone_interaction,
                        max_interaction_duration: fullCompareToData.max_interaction_duration,
                        max_interaction_zone: fullCompareToData.max_interaction_zone,
                        most_typical_interaction_time: fullCompareToData.most_typical_interaction_time,
                        real_transaction_rate: fullCompareToData.real_transaction_rate,
                        real_transaction_count: fullCompareToData.real_transaction_count,
                        fastest_checkout_zone: fullCompareToData.fastest_checkout_zone,
                        slowest_checkout_zone: fullCompareToData.slowest_checkout_zone,
                        fastest_interaction_zone: fullCompareToData.fastest_interaction_zone,
                        slowest_interaction_zone: fullCompareToData.slowest_interaction_zone,
                        most_used_interaction_zone: fullCompareToData.most_used_interaction_zone,
                        least_used_interaction_zone: fullCompareToData.least_used_interaction_zone,

                        // Capacity gauge delta vs previous period.
                        checkout_interaction_usage_pct: fullCompareToData.checkout_interaction_usage_pct,
                        checkout_cashier_usage_pct: fullCompareToData.checkout_cashier_usage_pct,
                        total_opening_hours: fullCompareToData.total_opening_hours,

                        date: fullCompareToData.last_updated_date // Add today's date to track when the data was fetched
                    }

                    fixedWidgetCompareToData.value = scenarioed({ ...newStoredCompareToData })
                    // console.log(fixedWidgetCompareToData.value)

                } catch (error) {
                    console.error('Error fetching fixed widget compare to... data:', error)
                }

        }

        return {
            dateRangeMode,
            clearDatePicker,
            dataGranularity,
            fixedWidgetData,
            fixedWidgetCompareToData,
            isLoading,
        }
    }
})
</script>
  
<style scoped>
@keyframes fadeInOut {
    0% { opacity: 0.4; }
    100% { opacity: 0.7; }
}

.loader {
    width: 80px;
    padding: 8px;
    aspect-ratio: 1;
    border-radius: 50%;
    background: #B2C149;
    --_m: 
        conic-gradient(#0000 10%,#000),
        linear-gradient(#000 0 0) content-box;
    -webkit-mask: var(--_m);
            mask: var(--_m);
    -webkit-mask-composite: source-out;
            mask-composite: subtract;
    animation: spinner 1s infinite linear;
}
@keyframes spinner {to{transform: rotate(1turn)}}

/* Mobile: stack title (row 1) and button (row 2) */
@media (max-width: 1080px){
  .ask-allen-btn{ margin-left: 0; width: max-content; }
}

</style>